import { Check, Clear } from "@procore/core-icons";
import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { CloseIconButton } from "features/quickAccessMenu/procore/closeIconButton";
import { ConfirmIconButton } from "features/quickAccessMenu/procore/confirmIconButton";
import { useCancelCurrentModeChanges } from "features/toolMode/useCancelCurrentModeChanges";
import { useConfirmCurrentModeChanges } from "features/toolMode/useConfirmCurrentModeChanges";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { Toolbar } from "../Toolbar";

interface ToolModeConfirmationToolbarProps {
    id?: string;
}

/**
 * Toolbar with close button that appears when measurement and section tools are active
 */
export function ToolModeConfirmationToolbar({
    id = "tool-mode-confirmation-toolbar",
}: ToolModeConfirmationToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const confirm = useConfirmCurrentModeChanges();
    const cancel = useCancelCurrentModeChanges();
    const activeToolMode = useAppSelector(selectActiveToolMode);

    const shouldShowToolbar = activeToolMode !== ProcoreToolMode.Default;
    const buttons = useMemo(
        () => [
            {
                config: {
                    id: "confirm-picker",
                    icon: <Check fontSize="large" />,
                    onClick: confirm,
                    title: "Confirm",
                    component: ConfirmIconButton,
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: shouldShowToolbar,
                },
            },
            {
                config: {
                    id: "close-picker",
                    icon: <Clear fontSize="large" />,
                    onClick: cancel,
                    title: "Close",
                    component: CloseIconButton,
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: shouldShowToolbar,
                },
            },
        ],
        [confirm, cancel, shouldShowToolbar],
    );

    useEffect(() => {
        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons }));

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, buttons]);

    return <Toolbar id={id} />;
}
